import { useState } from "react";

import { DatasetPurpose } from "src/api/types";
import { Button } from "src/base-components/Button";
import { FormItem } from "src/base-components/FormItem";
import { useDatasets } from "src/datasets/api/queries";
import { Modal } from "src/design-system/Modal";
import { useAuthoringContext } from "src/router/routerContextHooks";
import {
  LeftPaneOptions,
  useSetLeftSidePaneOption,
} from "src/router/SearchParams";
import { useStartSimulationRun } from "src/simulations/api/mutations";
import { SelectDatasetDropdown } from "src/simulations/SelectDatasetDropdown";

export const CreateSimulationModal = ({
  open,
  onClose,
  onAssembleDataset,
}: {
  open: boolean;
  onClose: () => void;
  onAssembleDataset: (purpose: DatasetPurpose) => void;
}) => {
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const { workspace, flow, version } = useAuthoringContext();
  const { data: allDatasets } = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  const setLeftSidePaneOption = useSetLeftSidePaneOption();

  const handleCreateDataset = () => {
    onClose();
    setLeftSidePaneOption(LeftPaneOptions.Datasets);
    onAssembleDataset("simulation");
  };

  const { mutateAsync: runSimulation, isPending } = useStartSimulationRun(
    workspace.base_url!,
  );

  const handleRunSimulation = async () => {
    if (selectedDataset) {
      const dataset = allDatasets?.find(
        (dataset) => dataset.id === selectedDataset,
      );
      if (dataset) {
        await runSimulation({
          flow_version_etag: version.etag!,
          flow_slug: flow.slug,
          flow_id: flow.id,
          dataset_id: dataset.id,
          flow_version_id: version.id,
        });
      }
      onClose();
    }
  };

  return (
    <Modal
      afterLeave={() => setSelectedDataset(null)}
      open={open}
      size="sm"
      onClose={onClose}
    >
      <Modal.Header>Create simulation</Modal.Header>
      <Modal.Content>
        <div className="flex w-full flex-col">
          <FormItem
            description="Choose the dataset you want to run a simulation with"
            label="Dataset"
          >
            <SelectDatasetDropdown
              selectedDataset={selectedDataset}
              onCreateDataset={handleCreateDataset}
              onSelectDataset={setSelectedDataset}
            />
          </FormItem>
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            disabled={!selectedDataset}
            loading={isPending}
            variant="primary"
            onClick={handleRunSimulation}
          >
            Run simulation
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
